import { useLayoutEffect, useRef, type RefObject } from 'react';

/**
 * The case study flow's motion, from `docs/case-studies/intake-prototype.html`: a picked card turns into
 * its chip at the top, "Something else" grows out of its card into one big input, and the answers at the
 * top make room for each other. Text itself never travels.
 */

export const EASE = 'cubic-bezier(.16, 1, .3, 1)';
/** A strong ease-out for something answering the person: quick off the mark, soft landing, no overshoot. */
export const EASE_OUT = 'cubic-bezier(.23, 1, .32, 1)';
/** "Something else" opening, and folding back: quick, and the fold quicker still, since it is a way out. */
export const GROW_MS = 260;
export const FOLD_MS = 180;
/** How long things beside a new arrival take to make room for it. */
export const MAKE_ROOM_MS = 320;

export const reducedMotion = () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/** A picked card, caught as it was picked: where it was, a copy of it, and how its box looked. */
export interface CaughtCard {
  rect: DOMRect;
  copy: HTMLElement;
  background: string;
  shadow: string;
  radius: string;
}

export function catchCard(card: HTMLElement): CaughtCard {
  const style = getComputedStyle(card);
  return {
    rect: card.getBoundingClientRect(),
    copy: card.cloneNode(true) as HTMLElement,
    background: style.backgroundColor,
    shadow: style.boxShadow,
    radius: style.borderTopLeftRadius,
  };
}

export const LIFT_MS = 320;
/**
 * The iOS sheet curve: it answers the click at once, covers most of the way early and settles softly, so
 * the trip is easy to follow without feeling slow.
 */
const LIFT_EASE = 'cubic-bezier(.32, .72, 0, 1)';

/**
 * The picked card becomes its chip at the top. One box travels from the card to the chip, its size,
 * corners and shadow turning into the chip's. Inside it the card's face fades and blurs away in the first
 * half and the chip's face fades in for the second, so no text is ever squeezed or seen travelling. The
 * real chip waits hidden until the box lands on it. The box is drawn in the flow's own layer
 * (`data-motion-layer`), so it is styled like the flow; while it flies the layer carries `data-lifting`.
 */
export function liftCard(from: CaughtCard, chip: HTMLElement) {
  if (reducedMotion()) return;
  const layer = chip.closest<HTMLElement>('[data-motion-layer]') ?? document.body;
  // A layer drawn scaled (the landing's tour plays the flow smaller) holds the box in its own pixels, from its
  // own corner; at full size the box is fixed to the window, where the card and the chip were measured.
  const base = layer.getBoundingClientRect();
  const scale = layer.offsetWidth ? base.width / layer.offsetWidth : 1;
  const scaled = Math.abs(scale - 1) > 0.01;
  const own = (rect: DOMRect) =>
    scaled ? new DOMRect((rect.left - base.left) / scale, (rect.top - base.top) / scale, rect.width / scale, rect.height / scale) : rect;
  const start = own(from.rect);
  const to = own(chip.getBoundingClientRect());
  const landing = getComputedStyle(chip);

  const box = document.createElement('div');
  Object.assign(box.style, { position: scaled ? 'absolute' : 'fixed', zIndex: '100', overflow: 'hidden', pointerEvents: 'none', boxSizing: 'border-box' });
  const unboxed = { position: 'absolute', left: '0', top: '0', margin: '0', background: 'transparent', boxShadow: 'none', animation: 'none' };
  const card = from.copy;
  Object.assign(card.style, unboxed, { width: `${start.width}px`, height: `${start.height}px`, transformOrigin: '0 0' });
  const face = chip.cloneNode(true) as HTMLElement;
  Object.assign(face.style, unboxed, { width: `${to.width}px`, height: `${to.height}px`, visibility: 'visible' });
  box.append(card, face);
  layer.appendChild(box);
  chip.style.visibility = 'hidden';
  layer.dataset.lifting = '';

  const timing = { duration: LIFT_MS, easing: LIFT_EASE, fill: 'both' as const };
  const trip = box.animate(
    [
      {
        left: `${start.left}px`,
        top: `${start.top}px`,
        width: `${start.width}px`,
        height: `${start.height}px`,
        borderRadius: from.radius,
        boxShadow: from.shadow,
        backgroundColor: from.background,
      },
      {
        left: `${to.left}px`,
        top: `${to.top}px`,
        width: `${to.width}px`,
        height: `${to.height}px`,
        // Half its height, not the chip's 9999px, or the card would turn into a pill on the first frame.
        borderRadius: `${to.height / 2}px`,
        boxShadow: landing.boxShadow,
        backgroundColor: landing.backgroundColor,
      },
    ],
    timing,
  );
  card.animate([{ transform: 'scale(1)' }, { transform: `scale(${to.width / start.width})` }], timing);
  card.animate([{ opacity: 1, filter: 'blur(0)' }, { opacity: 0, filter: 'blur(4px)' }], { duration: LIFT_MS * 0.4, easing: 'ease-out', fill: 'both' });
  // Starts just before the card's face is gone, so the box is never empty on the way.
  face.animate([{ opacity: 0 }, { opacity: 1 }], { duration: LIFT_MS * 0.45, delay: LIFT_MS * 0.3, easing: 'ease-out', fill: 'both' });

  const land = () => {
    chip.style.visibility = '';
    delete layer.dataset.lifting;
    box.remove();
  };
  trip.onfinish = land;
  trip.oncancel = land;
}

/**
 * When something is added to or taken out of a row, the things already in it slide to their new places
 * instead of jumping there. Children opt in with `data-glide="<key>"`. A slide only starts when a child's
 * resting place changes, and it starts from wherever the child is on screen, so another render mid-slide
 * neither restarts it nor makes it jump.
 */
const GLIDE = 'make-room';

export function useMakeRoom(row: RefObject<HTMLElement>) {
  const was = useRef(new Map<string, number>());
  useLayoutEffect(() => {
    // A row that is not on screen starts over next time, rather than sliding from where it once was.
    if (!row.current) {
      was.current = new Map();
      return;
    }
    const now = new Map<string, number>();
    row.current.querySelectorAll<HTMLElement>('[data-glide]').forEach((child) => {
      const key = child.dataset.glide ?? '';
      const onScreen = child.getBoundingClientRect().left;
      const sliding = child.getAnimations().find((animation) => animation.id === GLIDE);
      // Its resting place: where it is, less what a slide still in progress is adding.
      const offset = sliding ? new DOMMatrix(getComputedStyle(child).transform).m41 : 0;
      const rest = onScreen - offset;
      now.set(key, rest);
      const before = was.current.get(key);
      if (before === undefined || Math.abs(before - rest) < 0.5 || reducedMotion()) return;
      // On screen it is still where it was: mid-slide wherever the slide has got to, otherwise its old place.
      const from = (sliding ? onScreen : before) - rest;
      sliding?.cancel();
      const slide = child.animate([{ transform: `translateX(${from}px)` }, { transform: 'none' }], {
        duration: MAKE_ROOM_MS,
        easing: EASE_OUT,
      });
      slide.id = GLIDE;
    });
    was.current = now;
  });
}

/**
 * The clip that shows only `inner` of `outer`, so a big element can look like it is still the small one. The
 * rects are on screen; `scale` is how much the element is scaled there (the landing draws the flow smaller),
 * since a clip is measured in the element's own pixels.
 */
export function windowOnto(inner: DOMRect, outer: DOMRect, radius: number, scale = 1) {
  const px = (n: number) => `${n / scale}px`;
  return `inset(${px(inner.top - outer.top)} ${px(outer.right - inner.right)} ${px(outer.bottom - inner.bottom)} ${px(inner.left - outer.left)} round ${radius}px)`;
}
